import { Button, Form, Input, Select, message, notification } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PageBreadcrumb from "../../components/breadcrumb";
import { useAddBankMutation } from "../../services/banks";
import { useGetOpenBranchesQuery } from "../../services/branches";
import { Home, AllBankRoute } from "../../routes/routepath";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AddBankFormValues {
  bankName:      string;
  accountNumber: string;
  ifscCode:      string;
  // branchCity:    string;
  branches:      string[];
}

const breadcrumbItems = [
  { label: <HomeOutlined />, to: Home },
  { label: "Add Bank" },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const AddBank = () => {
  const [form]   = Form.useForm<AddBankFormValues>();
  const navigate = useNavigate();
  const [addBank, { isLoading: submitting }] = useAddBankMutation();
  const { data: branchesResp, isFetching: branchesLoading } = useGetOpenBranchesQuery();

  const branchList = Array.isArray(branchesResp)
    ? branchesResp
    : (Array.isArray((branchesResp as any)?.data) ? (branchesResp as any).data : []);
  const branchOptions = branchList.map((b: any) => ({
    value: b._id || b.id,
    label: b.name,
  }));

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message:     'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement:   'topRight',
      duration:    4,
    });
  };

  const onFinish = async (values: AddBankFormValues) => {
    const { branches, ...rest } = values;
    const branchIds = branches.length === 1 ? branches[0] : branches;
    try {
      await addBank({ ...rest, branchIds }).unwrap();
      // message.success('Bank added successfully');
      form.resetFields();
      navigate(AllBankRoute);
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  return (
    <div className="add-bank-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Add Bank</h2>
        <PageBreadcrumb items={breadcrumbItems} />
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed} className="add-bank-form">
        <Form.Item name="bankName" label="Bank Name" rules={[{ required: true, message: 'Please enter bank name' }]}>
          <Input placeholder="e.g. HDFC Bank" />
        </Form.Item>

        <Form.Item name="accountNumber" label="Account Number" rules={[{ required: true, message: 'Please enter account number' }]}>
          <Input placeholder="Enter account number" />
        </Form.Item>

        <Form.Item name="ifscCode" label="IFSC Code" rules={[{ required: true, message: 'Please enter IFSC code' }]}>
          <Input placeholder="e.g. HDFC0001234" style={{ textTransform: 'uppercase' }} />
        </Form.Item>

        {/* <Form.Item name="branchCity" label="Branch City" rules={[{ required: true, message: 'Please enter branch city' }]}>
          <Input placeholder="e.g. Gurgaon" />
        </Form.Item> */}

        <Form.Item name="branches" label="Branch" rules={[{ required: true, message: 'Please select at least one branch' }]}>
          <Select
            mode="multiple"
            placeholder="Select branch(es)"
            loading={branchesLoading}
            showSearch
            optionFilterProp="label"
            options={branchOptions}
          />
        </Form.Item>

        <div className="footer-buttons">
          <Button type="default" onClick={() => navigate(AllBankRoute)}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" className="save-btn" loading={submitting}>
            {submitting ? 'Adding...' : 'Add Bank'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddBank;
